import React, { useState, useEffect } from 'react'
import httpHelper from 'utils/http-helper'
import URL_CONSTANTS from 'constants/url-constants'
import { Method_TYPE } from 'utils/http-helper'
import DataTable from 'react-data-table-component'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import ReportHeader from './ReportHeader'
import { ArrowUpDown } from 'lucide-react'
import { convertToIST } from 'utils/formtaDate'
import {
  SEVADAL_SORT_OPTIONS,
  SEVADAL_SEARCH_FIELDS,
  useFilteredSortedData,
  makeSortableColumn,
  makePlainColumn,
  ReportTableControls,
  ExportButtons,
  createColumnSortHandler,
  reportTableStyles,
} from './reportTableUtils'

export default function SevadalSamithiReg() {
  const [selectedSamithi, setSelectedSamithi] = useState('')
  const [samithiValues, setSamithiValues] = useState([])
  const [sevadalDetails, setSevadalDetails] = useState([])
  const [tableSearchTerm, setTableSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('sector')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    getSamithiValue()
  }, [])

  const getSamithiValue = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_SAMITHI_VALUES, Method_TYPE.GET)
      setSamithiValues(data?.data || [])
    } catch (err) {
      console.error('Error fetching samithi values:', err)
      setSamithiValues([])
    }
  }

  const handleSamithiChange = (e) => {
    setSelectedSamithi(e.target.value)
  }

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'))
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`
    }
    return ''
  }

  const handleSearchSevadal = async () => {
    try {
      const data = await httpHelper(`${URL_CONSTANTS.GET_SEVADAL_DETAILS}?samithi=${selectedSamithi}`, Method_TYPE.GET)
      const resultSet = data?.reports?.sevaDalList?.map((tableData) => ({
        name: tableData?.Name,
        district: tableData?.district,
        samithi: tableData?.samithi,
        mobile: tableData?.mobNo,
        saiconnectedid: tableData?.saiOneId,
        sector: tableData?.Sector,
        deploymentpoint: tableData?.DutyPoint,
      })) || []
      setSevadalDetails(resultSet)
    } catch (err) {
      console.error('Error fetching Sevadal details:', err)
      setSevadalDetails([])
    }
  }

  const handleColumnSort = createColumnSortHandler(sortBy, setSortBy, sortDirection, setSortDirection)

  const filteredAndSortedData = useFilteredSortedData(sevadalDetails, {
    searchTerm: tableSearchTerm,
    searchableFields: SEVADAL_SEARCH_FIELDS,
    sortBy,
    sortDirection,
  })

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    makeSortableColumn('Name', 'name', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('District', 'district', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('Samithi', 'samithi', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Mobile', 'mobile'),
    makePlainColumn('Sai Connect', 'saiconnectedid'),
    makeSortableColumn('Sector', 'sector', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Deployment Point', 'deploymentpoint'),
  ]

  const handlePrint = () => {
    const printContent = document.getElementById('print-table').innerHTML
    const printWindow = window.open('', '', 'height=600,width=800')
    printWindow.document.write('<html><head><title>Print</title></head><body>')
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Sevadal Report (Samithi Wise)</h2><p><strong>${getBatchInfo()}</strong></p>`)
    printWindow.document.write(printContent)
    printWindow.document.write('</body></html>')
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  const handleDownloadPDF = () => {
    const doc = new jsPDF()
    doc.text('Sevadal Samithi Report', 14, 10)
    doc.text(`${getBatchInfo()}`, 14, 18)
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'Name', 'District', 'Samithi', 'Mobile', 'Sai Connect', 'Sector', 'Deployment Point']],
      body: filteredAndSortedData.map((row, index) => [
        index + 1,
        row.name,
        row.district,
        row.samithi,
        row.mobile,
        row.saiconnectedid,
        row.sector,
        row.deploymentpoint,
      ]),
    })
    doc.save('sevadal-samithi-report.pdf')
  }

  const handleDownloadXLS = () => {
    const exportData = filteredAndSortedData.map((item, index) => ({
      'Sl.No': index + 1,
      Name: item.name,
      District: item.district,
      Samithi: item.samithi,
      Mobile: item.mobile,
      'Sai Connect': item.saiconnectedid,
      Sector: item.sector,
      'Deployment Point': item.deploymentpoint,
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sevadal Report')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' })
    saveAs(data, 'sevadal-samithi-report.xlsx')
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="samithi-select" className="font-medium whitespace-nowrap">Select Samithi:</label>
          <select
            id="samithi-select"
            className="p-2 md:min-w-[200px] w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
            value={selectedSamithi}
            onChange={handleSamithiChange}
            disabled={samithiValues.length === 0}
          >
            <option value="">Select Samithi</option>
            {samithiValues?.map((samithiValue) => (
              <option key={samithiValue?._id} value={samithiValue?._id}>
                {samithiValue?.samithiName}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleSearchSevadal}
          className="bg-primary text-primary-foreground text-white px-4 py-2 rounded hover:bg-accent"
          disabled={!selectedSamithi}
        >
          Search
        </button>
      </div>

      <ReportTableControls
        searchTerm={tableSearchTerm}
        onSearchChange={setTableSearchTerm}
        searchPlaceholder="Search name, district, samithi, sector..."
        sortBy={sortBy}
        onSortByChange={(value) => {
          setSortBy(value)
          setSortDirection('asc')
        }}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        sortOptions={SEVADAL_SORT_OPTIONS}
        totalCount={sevadalDetails.length}
        filteredCount={filteredAndSortedData.length}
        actions={
          <ExportButtons
            show={sevadalDetails.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
        hint={
          <>
            Click column headers with{' '}
            <ArrowUpDown size={12} className="inline mx-0.5 text-slate-400" /> to sort by Name, District,
            Samithi, or Sector
          </>
        }
      />

      <ReportHeader header="Sevadal Samithi Registration" />
      <DataTable
        columns={columns}
        data={filteredAndSortedData}
        fixedHeader
        fixedHeaderScrollHeight="500px"
        className="reports-table"
        customStyles={reportTableStyles}
        noDataComponent={
          <div className="py-10 text-sm text-slate-500">
            {tableSearchTerm.trim() ? 'No rows match your search' : 'No sevadal details available'}
          </div>
        }
      />

      <div id="print-table" style={{ display: 'none' }}>
        <table border="1" cellPadding="5" cellSpacing="0" width="100%">
          <thead>
            <tr>
              <th>Sl.No</th>
              <th>Name</th>
              <th>District</th>
              <th>Samithi</th>
              <th>Mobile</th>
              <th>Sai Connect</th>
              <th>Sector</th>
              <th>Deployment Point</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{item.district}</td>
                <td>{item.samithi}</td>
                <td>{item.mobile}</td>
                <td>{item.saiconnectedid}</td>
                <td>{item.sector}</td>
                <td>{item.deploymentpoint}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
